// Хук записи голоса (клик-старт/клик-стоп, мобильные фиксы, без "тихого" gUM на маунте)
import { useEffect, useRef, useState } from "react";

const ORDER_CHROME = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
    "audio/mpeg",
];
const ORDER_SAFARI = [
    "audio/mp4",              // iOS/iPadOS/macOS Safari => m4a/aac
    "audio/mpeg",             // mp3 (на случай экзотики)
    "audio/webm;codecs=opus", // вдруг есть
    "audio/webm",
];

function isSafariUA() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /Safari/.test(ua) && !/Chrome|Chromium|Edg/.test(ua);
}

function pickBestMime(order) {
    for (const t of order) {
        try {
            if (window.MediaRecorder && MediaRecorder.isTypeSupported?.(t)) return t;
        } catch { }
    }
    return ""; // пускай браузер решит
}

export function useVoiceRecorder({
    maxMs = 5 * 60 * 1000,
    deviceId,                // выбранный микрофон
    onDevices,               // (devices[]) => void
    onLevel,                 // (0..1) => void
    onStop,                  // ({ blob, mime, durationMs }) => void
} = {}) {
    const [available, setAvailable] = useState(!!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder));
    const [recording, setRecording] = useState(false);
    const [elapsedMs, setElapsedMs] = useState(0);
    const [devices, setDevices] = useState([]);

    const chunksRef = useRef([]);
    const recRef = useRef(null);
    const streamRef = useRef(null);
    const startedAtRef = useRef(0);
    const timerRef = useRef(null);

    // анализ уровня
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const sourceRef = useRef(null);
    const dataRef = useRef(null);
    const rafRef = useRef(null);

    const order = isSafariUA() ? ORDER_SAFARI : ORDER_CHROME;
    const mimeType = pickBestMime(order);

    useEffect(() => {
        setAvailable(!!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder));
    }, []);

    const enumerate = async () => {
        if (!navigator.mediaDevices?.enumerateDevices) return;
        try {
            const list = await navigator.mediaDevices.enumerateDevices();
            const mics = list.filter(d => d.kind === "audioinput");
            setDevices(mics);
            onDevices?.(mics);
        } catch { }
    };

    // просто читаем список устройств; не просим доступ «тихо» (чтоб не горел индикатор)
    useEffect(() => {
        enumerate();
        const onDev = () => enumerate();
        navigator.mediaDevices?.addEventListener?.("devicechange", onDev);
        return () => navigator.mediaDevices?.removeEventListener?.("devicechange", onDev);
    }, []);

    const teardownAnalyser = () => {
        try { cancelAnimationFrame(rafRef.current); } catch { }
        rafRef.current = null;
        try { sourceRef.current?.disconnect(); } catch { }
        sourceRef.current = null;
        try { analyserRef.current?.disconnect(); } catch { }
        analyserRef.current = null;
        dataRef.current = null;
        try { audioCtxRef.current?.close(); } catch { }
        audioCtxRef.current = null;
    };

    const tickLevel = () => {
        if (!analyserRef.current || !dataRef.current) return;
        analyserRef.current.getByteTimeDomainData(dataRef.current);
        let peak = 0;
        for (let i = 0; i < dataRef.current.length; i++) {
            const v = Math.abs(dataRef.current[i] - 128) / 128;
            if (v > peak) peak = v;
        }
        onLevel?.(peak);
        rafRef.current = requestAnimationFrame(tickLevel);
    };

    const hardStopStream = () => {
        try { streamRef.current?.getTracks?.().forEach(t => t.stop()); } catch { }
        streamRef.current = null;
        teardownAnalyser();
    };

    // авто-стоп при уходе со страницы/сворачивании
    useEffect(() => {
        const stopIf = () => { if (recording) stop(); };
        document.addEventListener("visibilitychange", stopIf);
        window.addEventListener("pagehide", stopIf);
        window.addEventListener("blur", stopIf);
        return () => {
            document.removeEventListener("visibilitychange", stopIf);
            window.removeEventListener("pagehide", stopIf);
            window.removeEventListener("blur", stopIf);
        };
    }, [recording]);

    const start = async () => {
        if (!available || recording) return;

        // подчистим, если вдруг что-то осталось
        hardStopStream();

        const audioConstr = deviceId
            ? { deviceId: { exact: deviceId }, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
            : { echoCancellation: true, noiseSuppression: true, autoGainControl: true };

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstr });
            streamRef.current = stream;

            // вдруг после разрешения появились лейблы
            enumerate();

            const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            window.__lastMediaRecorder = rec;
            recRef.current = rec;
            chunksRef.current = [];

            rec.ondataavailable = (e) => { if (e?.data?.size) chunksRef.current.push(e.data); };
            rec.onstart = () => {
                startedAtRef.current = Date.now();
                setElapsedMs(0);
                setRecording(true);
                clearInterval(timerRef.current);
                timerRef.current = setInterval(() => {
                    const ms = Date.now() - startedAtRef.current;
                    setElapsedMs(ms);
                    if (ms >= maxMs) stop();
                }, 200);
            };
            rec.onstop = () => {
                clearInterval(timerRef.current);
                setRecording(false);
                const blob = new Blob(chunksRef.current, { type: rec.mimeType || mimeType || "audio/webm" });
                chunksRef.current = [];
                const durationMs = Math.max(0, Date.now() - (startedAtRef.current || Date.now()));
                onStop?.({ blob, mime: blob.type || rec.mimeType || mimeType, durationMs });
                hardStopStream();
                setElapsedMs(0);
            };
            rec.onerror = () => { }; // не шумим

            // Safari чувствительнее к timeslice — стартуем без него (кусок придёт по onstop)
            rec.start();

            // анализатор уровня
            audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
            analyserRef.current = audioCtxRef.current.createAnalyser();
            analyserRef.current.fftSize = 512;
            dataRef.current = new Uint8Array(analyserRef.current.fftSize);
            sourceRef.current = audioCtxRef.current.createMediaStreamSource(stream);
            sourceRef.current.connect(analyserRef.current);
            tickLevel();
        } catch {
            // молча игнорируем — пользователь мог запретить доступ
        }
    };

    const stop = () => {
        if (!recording) { hardStopStream(); return; }
        try { recRef.current?.stop(); } catch { hardStopStream(); }
    };

    // для удобства — единая кнопка
    const toggle = () => (recording ? stop() : start());

    useEffect(() => () => {
        try { recRef.current?.state === "recording" && recRef.current.stop(); } catch { }
        hardStopStream();
        clearInterval(timerRef.current);
    }, []);

    return { available, recording, elapsedMs, devices, start, stop, toggle };
}
